/**
 * React Query hooks для admin backfill UI (task 17 §3.11).
 *
 * `useAdminTimesheet(userId, date)` lazy-creates draft на бэке — поэтому query
 * выключен пока `userId === null` или `date === null` (иначе создались бы
 * пустые draft'ы при каждом рендере страницы без выбранного рабочего).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import {
  createAdminEntry,
  deleteAdminEntry,
  getAdminTimesheet,
  listAdminTimesheetWorkers,
  listBackfillWorkTypes,
  listUserBackfillWorkTypes,
  listUserRecentProjects,
  replaceAdminEntry,
  signAdminTimesheet,
  type AdminSignPayload,
  type AdminTimesheetSnapshot,
  type AdminTimesheetWorker,
  type Project,
  type SignResponse,
  type TimesheetEntry,
  type TimesheetEntryPayload,
  type WorkType,
} from "@/api/admin-timesheet";
import { invalidateApprovalQueries } from "@/hooks/useApproval";

export const ADMIN_TIMESHEET_WORKERS_KEY = ["admin-timesheet-workers"] as const;
export const ADMIN_BACKFILL_WORK_TYPES_KEY = ["admin-backfill-work-types"] as const;

function adminTimesheetKey(userId: number, date: string) {
  return ["admin-timesheet", userId, date] as const;
}

function adminRecentProjectsKey(userId: number) {
  return ["admin-recent-projects", userId] as const;
}

function noRetryOn401(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return false;
  }
  return failureCount < 1;
}

export function useAdminTimesheetWorkers(enabled: boolean = true) {
  return useQuery<AdminTimesheetWorker[], Error>({
    queryKey: ADMIN_TIMESHEET_WORKERS_KEY,
    queryFn: listAdminTimesheetWorkers,
    retry: noRetryOn401,
    staleTime: 60_000,
    enabled,
  });
}

export function useAdminBackfillWorkTypes(enabled: boolean = true) {
  return useQuery<WorkType[], Error>({
    queryKey: ADMIN_BACKFILL_WORK_TYPES_KEY,
    queryFn: listBackfillWorkTypes,
    retry: noRetryOn401,
    staleTime: 60_000,
    enabled,
  });
}

/**
 * Виды работ выбранного сотрудника (active ∩ user_work_types) — шаг 1 единого
 * редактора записи (`EntryEditorCore`), чтобы выбор не предлагал виды работ,
 * которых нет у сотрудника. `useAdminBackfillWorkTypes` остаётся для `EntryList`
 * (name resolution существующих entries).
 */
export function useAdminBackfillUserWorkTypes(userId: number | null) {
  return useQuery<WorkType[], Error>({
    queryKey: ["admin-backfill-user-work-types", userId ?? -1] as const,
    queryFn: () => listUserBackfillWorkTypes(userId as number),
    retry: noRetryOn401,
    staleTime: 60_000,
    enabled: userId !== null,
  });
}

/**
 * «Горячие» проекты выбранного сотрудника — чипы-подсказки в шаге «проект»
 * единого редактора при вводе за сотрудника (§4.10). Отдельный query (не часть
 * `useAdminTimesheet`): recent меняется редко → `staleTime 60s`, тогда как
 * часовой snapshot обновляется агрессивно (`staleTime 5s`). `enabled` при
 * выбранном сотруднике, чтобы не дёргать ручку без `userId`.
 */
export function useAdminRecentProjects(userId: number | null) {
  return useQuery<Project[], Error>({
    queryKey: adminRecentProjectsKey(userId ?? -1),
    queryFn: () => listUserRecentProjects(userId as number),
    retry: noRetryOn401,
    staleTime: 60_000,
    enabled: userId !== null,
  });
}

/**
 * Виды работ сотрудника для единого редактора записи (`EntryEditorCore`,
 * задача 77). Плоский `WorkType[]` (шаг «элемент сборки» строит §4.2 задачи 89 —
 * per-project резолв, не привязан к виду работ). Общий для §3.1 (`EntryDock`) и
 * §4.1 (`AdminTimesheetBackfillPage`).
 */
export function useAdminEntryWorkTypes(userId: number | null): WorkType[] {
  const workTypes = useAdminBackfillUserWorkTypes(userId);
  return workTypes.data ?? [];
}

export function useAdminTimesheet(userId: number | null, date: string | null) {
  return useQuery<AdminTimesheetSnapshot, Error>({
    queryKey: adminTimesheetKey(userId ?? -1, date ?? ""),
    queryFn: () => getAdminTimesheet(userId as number, date as string),
    enabled: userId !== null && date !== null && date.length > 0,
    retry: noRetryOn401,
    staleTime: 5_000,
  });
}

export interface AdminEntryVars {
  userId: number;
  date: string;
  payload: TimesheetEntryPayload;
}

export interface AdminReplaceEntryVars extends AdminEntryVars {
  entryId: number;
}

export interface AdminDeleteEntryVars {
  userId: number;
  date: string;
  entryId: number;
}

export interface AdminSignVars {
  userId: number;
  date: string;
  payload: AdminSignPayload;
}

function invalidateTimesheet(qc: ReturnType<typeof useQueryClient>, userId: number, date: string) {
  void qc.invalidateQueries({ queryKey: adminTimesheetKey(userId, date) });
  invalidateApprovalQueries(qc);
}

export function useCreateAdminEntry() {
  const qc = useQueryClient();
  return useMutation<TimesheetEntry, Error, AdminEntryVars>({
    mutationFn: ({ userId, date, payload }) => createAdminEntry(userId, date, payload),
    onSuccess: (_data, { userId, date }) => {
      invalidateTimesheet(qc, userId, date);
      // Новый проект записи сразу попадает в чипы-подсказки (§4.10).
      void qc.invalidateQueries({ queryKey: adminRecentProjectsKey(userId) });
    },
  });
}

export function useReplaceAdminEntry() {
  const qc = useQueryClient();
  return useMutation<TimesheetEntry, Error, AdminReplaceEntryVars>({
    mutationFn: ({ userId, date, entryId, payload }) =>
      replaceAdminEntry(userId, date, entryId, payload),
    onSuccess: (_data, { userId, date }) => invalidateTimesheet(qc, userId, date),
  });
}

export function useDeleteAdminEntry() {
  const qc = useQueryClient();
  return useMutation<void, Error, AdminDeleteEntryVars>({
    mutationFn: ({ userId, date, entryId }) => deleteAdminEntry(userId, date, entryId),
    onSuccess: (_data, { userId, date }) => invalidateTimesheet(qc, userId, date),
  });
}

export function useSignAdminTimesheet() {
  const qc = useQueryClient();
  return useMutation<SignResponse, Error, AdminSignVars>({
    mutationFn: ({ userId, date, payload }) => signAdminTimesheet(userId, date, payload),
    onSuccess: (_data, { userId, date }) => invalidateTimesheet(qc, userId, date),
  });
}
